import queryString from 'query-string';

import { ENV } from '@shared/config';
import type { Reviews } from '@shared/types';
import { decodeHtmlEntities } from '@shared/utils';

// eslint-disable-next-line import/no-internal-modules
import { getClientNoCache } from '../libs/auth-context/token';

// Интерфейс для ответа сервера  
interface ServerReviewsResponse {
  result_code: number;
  data: Reviews;
  lang: string;
  userID: string | null;
  pager: {
    page_current: number;
    pages_all: number;
    records_all: number;
    records_per_page: number;
  };
}

// Интерфейс для возвращаемого значения функции
export interface ReviewsResponse {
  data: Reviews;
  pager: {
    page_current: number;
    pages_all: number;
    records_all: number;
    records_per_page: number;
  };
}

export async function getReviews({
  noteId,
  page = 1,
  pagelen = 5,
}: {
  noteId: string;
  page?: number;
  pagelen?: number;
}): Promise<ReviewsResponse> {
  const requestLink = `${ENV.API}/review_list/${noteId}/?${queryString.stringify({
    lang: ENV.LANG,
    page,
    pagelen,
  })}`;

  try {
    const noChach = await getClientNoCache();
    const res = await fetch(requestLink, {
      cache: noChach ? 'no-store' : undefined,
      next: noChach
        ? undefined
        : {
            revalidate: 5,
            tags: [`reviews-${noteId}`, `reviews-${noteId}-page-${page}`, 'ALL_REVIEWS'],
          },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}, statusText: ${res.statusText}`);
    }

    const response = await res.json() as ServerReviewsResponse;
    
    return {
      data: decodeHtmlEntities(response.data),
      pager: response.pager || {
        page_current: page,
        pages_all: 0,
        records_all: 0,
        records_per_page: pagelen,
      },
    };
  } catch (error) {
    const errorDetails = {
      error: 'Серверная ошибка получения отзывов о товаре',
      noteId,
      page,
      pagelen,
      url: requestLink,
      details: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      timestamp: new Date().toISOString(),
    };

    console.error('Ошибка получения reviews на сервере:', errorDetails);
    
    // В продакшене можно добавить отправку в внешний сервис логирования
    if (process.env.NODE_ENV === 'production') {
      // TODO: Добавить интеграцию с сервисом мониторинга (например, Sentry, LogRocket)
      // Пример: Sentry.captureException(error, { contexts: { reviewsServerError: errorDetails } });
    }
    
    // Возвращаем пустой ответ с базовой пагинацией при ошибке
    return {
      data: [],
      pager: {
        page_current: page,
        pages_all: 0,
        records_all: 0,
        records_per_page: pagelen,
      },
    };
  }
} 